import React, { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import { Group, Rect, Transformer } from 'react-konva'
import { updateToolbarStyle } from '../../context/SelectedVariantContext/helper'
import SpinnerLayer from './SpinnerLayer'
import { actionGetClientRect, actionUpdateImageLayerLoading } from '../../context/SelectedVariantContext/action'
import { PATTERN_TYPE } from '../../../../constant'
import { toaster } from '../../../../shared/PFToast'
import PubSub from '../../Broker'
import CropSelectorWithBounds from '../KonvaCropV2'
import {makeKonvaHandlers} from '../../../../utils/canvas-perf'

const ImageLayer = memo((props) => {
    const {
        imageProps,
        isSelected,
        onSelect,
        onChange,
        zIndexDefault,
        onChangeSizeImage,
        background,
        dispatch,
        rectRefs,
        layerId,
        cropMode,
        productCreatedType,
        selectedSide,
        // Side-level coverage coordinator — provided by MockupLayers via useSideCoverageCheck.
        // Accepts (layerId, image, imageProps).
        onSideCoverageCheck,
        // Called on unmount so coordinator removes this layer from its map.
        onSideCoverageUnregister,
    } = props

    const { isLock, patternType } = imageProps

    const imageRef = useRef(null)
    const trRef = useRef(null)
    const groupRef = useRef(null)

    const [image, setImage] = useState(null)
    const [loading, setLoading] = useState(false)
    const [grid, setGrid] = useState([])
    const [isDraggable, setIsDraggable] = useState(true)
    // Cache the pre-drawn OffscreenCanvas for the current artwork image.
    // Passed to the side-level coordinator so it can composite without re-decoding.
    const artOffscreenCanvasRef = useRef(null)

    // Notify the side-level coverage coordinator (useSideCoverageCheck in MockupLayers).
    // Passes this layer's latest image + placement so the coordinator can composite
    // ALL layers on this side before running the single worker check.
    // NOTE: we no longer pass artOffscreenCanvasRef — the coordinator owns the composite
    // canvas itself and draws each image directly via drawImage(entry.image, ...).
    const _notifyCoverageCheck = useCallback((img, placement) => {
        if (onSideCoverageCheck) {
            onSideCoverageCheck(layerId, img, placement)
        }
    }, [onSideCoverageCheck, layerId])

    // Unregister from coordinator on unmount so composite excludes deleted layers.
    useEffect(() => {
        return () => {
            if (onSideCoverageUnregister) onSideCoverageUnregister(layerId)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layerId])

    const perfHandlers = React.useMemo(() => makeKonvaHandlers(), [])

    useEffect(() => {
        if (!cropMode && isSelected && !loading && trRef.current) {
            const node = imageRef.current
            if (node) {
                trRef.current.nodes([node])
                trRef.current.getLayer()?.batchDraw()

                trRef.current.moveToTop()
                dispatch(actionGetClientRect(imageRef.current?.getClientRect()))
            }
        }
    }, [cropMode, isSelected, loading])

    useEffect(() => {
        setLoading(true)
        dispatch(actionUpdateImageLayerLoading(true))
        artOffscreenCanvasRef.current = null  // clear stale canvas for old src

        const img = new window.Image()
        img.crossOrigin = 'Anonymous'

        img.onload = () => {
            // Pre-draw into OffscreenCanvas once — transferToImageBitmap() per coverage check is ~0ms.
            // Done here (onload) so the ~20-50ms drawImage cost is paid once, not on every drag.
            try {
                const oc = new OffscreenCanvas(img.naturalWidth, img.naturalHeight)
                oc.getContext('2d').drawImage(img, 0, 0)
                artOffscreenCanvasRef.current = oc
            } catch (_) {
                artOffscreenCanvasRef.current = null  // OffscreenCanvas not supported → fallback
            }
            const timer = setTimeout(() => {
                setImage(img)
                setLoading(false)
                dispatch(actionUpdateImageLayerLoading(false))
                _notifyCoverageCheck(img, imageProps)
            }, 100)
            return () => {
                clearTimeout(timer)
            }
        }
        img.onerror = (e) => {
            setLoading(false)
            toaster({ type: 'error', message: 'Error loading image' + e, duration: 6000 })
        }

        img.src = imageProps.src
    }, [imageProps.src, dispatch])

    useEffect(() => {
        if (imageRef.current && rectRefs && layerId) {
            rectRefs.current.set(layerId, imageRef.current)

            // Cleanup function to remove ref when component unmounts
            return () => {
                rectRefs.current.delete(layerId)
            }
        }
    }, [imageProps, imageRef.current, groupRef.current, layerId])

    const getAdjustedSize = useCallback(
        (scaleFactor = 1) => {
            const { width: imageWidth, height: imageHeight, scaleX, scaleY } = imageProps
            if (scaleX && scaleY) {
                const tempScaleX = scaleX < 0 ? -scaleX : scaleX
                const tempScaleY = scaleY < 0 ? -scaleY : scaleY

                scaleFactor = Math.min(tempScaleX, tempScaleY) || 1
            }

            const adjustedWidth = imageWidth * scaleFactor
            const adjustedHeight = imageHeight * scaleFactor
            const adjustedRows = Math.ceil(background.width / adjustedWidth)
            let adjustedCols = Math.ceil(background.height / adjustedHeight)
            if (adjustedCols % 2 === 0) {
                adjustedCols = adjustedCols + 1
            }
            return { adjustedRows, adjustedCols, adjustedWidth, adjustedHeight }
        },
        [imageProps, background]
    )

    const renderGrid = useCallback(
        (rows = 2, cols = 2) => {
            let matrix = []
            let scaleFactor = 1
            const {
                x: imageX,
                y: imageY,
                hasPattern,
                patternType,
                horizontalSpacing = 0,
                verticalSpacing = 0,
            } = imageProps

            if (hasPattern) {
                const { adjustedRows, adjustedCols, adjustedWidth, adjustedHeight } = getAdjustedSize(scaleFactor)
                if (patternType === PATTERN_TYPE.HORIZONTAL) {
                    for (let i = -adjustedRows; i <= adjustedRows; i++) {
                        let x = imageX - i * (adjustedWidth + Number(horizontalSpacing) / background.ratioDefault)
                        if (x === 0) continue
                        matrix.push({
                            ...imageProps,
                            y: imageY,
                            x,
                        })
                    }
                } else if (patternType === PATTERN_TYPE.VERTICAL) {
                    for (let i = -adjustedCols; i <= adjustedCols; i++) {
                        let y = imageY - i * (adjustedHeight + Number(verticalSpacing) / background.ratioDefault)
                        if (y === 0) continue
                        matrix.push({
                            ...imageProps,
                            fillPatternImage: image,
                            x: imageX,
                            y: y,
                        })
                    }
                } else {
                    for (let i = -adjustedRows; i <= adjustedRows; i++) {
                        for (let j = -adjustedCols; j < adjustedCols; j++) {
                            if (i === 0 && j === 0) continue
                            let x = imageX - i * (adjustedWidth + Number(horizontalSpacing) / background.ratioDefault)
                            let y = imageY - j * (adjustedHeight + Number(verticalSpacing) / background.ratioDefault)
                            if (patternType === PATTERN_TYPE.HORIZONTAL_BRICK) {
                                x += j % 2 === 0 ? 0 : adjustedWidth / 2
                            }
                            if (patternType === PATTERN_TYPE.VERTICAL_BRICK) {
                                y += i % 2 === 0 ? 0 : adjustedHeight / 2
                            }

                            matrix.push({
                                ...imageProps,
                                isPattern: true,
                                fillPatternImage: image,
                                x: x,
                                y: y,
                                name: `rect_${i}_${j}`,
                                key: `rect_${i}_${j}`,
                                mainElement: i === 0 && j === 0,
                            })
                        }
                    }
                }
            } else {
                matrix = []
            }
            return matrix
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [imageProps, background, imageProps.hasPattern]
    )

    useEffect(() => {
        setGrid(renderGrid())
    }, [renderGrid])

    useEffect(() => {
        const imageNode = imageRef.current
        if (imageNode) {
            imageNode.on('mouseenter mouseup dragend', () => {
                imageNode.getStage().container().style.cursor = 'grab'
            })
            imageNode.on('mouseleave', () => {
                imageNode.getStage().container().style.cursor = 'default'
            })
            imageNode.on('mousedown', () => {
                imageNode.getStage().container().style.cursor = 'grabbing'
            })

            return () => {
                imageNode.off('mouseenter')
                imageNode.off('mouseup')
                imageNode.off('dragend')
                imageNode.off('mouseleave')
                imageNode.off('mousedown')
            }
        }
    }, [loading])

    const onShowToolbar = useCallback(() => {
        const trNode = trRef.current
        if (trNode && imageProps) {
            const rotateIcon = trNode.findOne('.rotater')
            const pos = rotateIcon.getAbsolutePosition()
            const { rotation } = imageProps
            const rect = imageRef.current?.getClientRect()
            updateToolbarStyle({ ...pos, height: rect.height / 2, rotation }, 'image-toolbar')
        }
    }, [imageProps])

    useEffect(() => {
        const imageNode = imageRef.current
        const trNode = trRef.current
        if (imageNode && trNode) {
            if (isSelected) {
                trNode.moveToTop()
                trNode.nodes([imageNode])
                !isLock && onShowToolbar()
            } else {
                imageNode.zIndex(zIndexDefault)
                onHideToolbar()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, isLock, zIndexDefault, loading])

    const onItemClick = () => {
        const imageNode = imageRef.current
        if (!imageNode) return

        onSelect({ ...imageProps })
        setTimeout(() => {
            onShowToolbar()
        }, 100)
    }

    const onDragStart = (event) => {
        // Check if drag starts outside background boundaries
        const stage = event.target.getStage()
        const { x: pointerX, y: pointerY } = stage.getPointerPosition()

        // Calculate position considering scale (zoom)
        const scaleX = stage.scaleX()
        const scaleY = stage.scaleY()
        const pos = {
            x: (pointerX - stage.x()) / scaleX,
            y: (pointerY - stage.y()) / scaleY,
        }

        // Check if click is within background bounds
        const { x: bgX, y: bgY, width: bgWidth, height: bgHeight } = background
        const isWithinBackground = pos.x >= bgX && pos.x <= bgX + bgWidth && pos.y >= bgY && pos.y <= bgY + bgHeight

        // If click is outside background bounds, disable draggable
        if (!isWithinBackground) {
            setIsDraggable(false)
            // Re-enable after a short delay to allow for future drags
            setTimeout(() => setIsDraggable(true), 100)
            return false
        }

        // If within bounds, proceed with normal drag start
        onItemClick()
    }

    const onHideToolbar = () => {
        const toolbar = document.getElementById('image-toolbar')
        toolbar.style.display = 'none'
    }

    const onDragEnd = (event) => {
        if (!event.target) return
        const attrs = event.target.attrs
        dispatch(actionGetClientRect(imageRef.current?.getClientRect()))
        onChange(attrs)
        onShowToolbar()
        if (image) _notifyCoverageCheck(image, attrs)
    }

    const onTransformEnd = (event) => {
        if (!event.target) return
        const imageAttrs = event.target.attrs
        onChange(imageAttrs)
        onShowToolbar()
        if (image) _notifyCoverageCheck(image, imageAttrs)
    }

    const onTransformRealTime = (event) => {
        const imageNode = event.target
        if (!imageNode) return
        const imageAttrs = imageNode.attrs
        if (imageAttrs.dpi === Infinity) return

        const size = {
            width: imageNode.width() || 1,
            height: imageNode.height() || 1,
            scaleX: imageNode.scaleX() || 1,
            scaleY: imageNode.scaleY() || 1,
        }
        onHideToolbar()
        rectRefs.current.set(layerId, imageRef.current)
        onChange(imageAttrs)
        onChangeSizeImage(size)
        dispatch(actionGetClientRect(imageRef.current?.getClientRect()))
    }

    const onDragMove = (event) => {
        if (!imageProps.hasPattern) return
        onShowToolbar()
        // Trigger coverage check while dragging pattern mode (group drag)
        if (image) {
            const imageNode = imageRef.current
            const groupNode = groupRef.current
            if (imageNode && groupNode) {
                const x = imageNode.attrs.x + groupNode.attrs.x
                const y = imageNode.attrs.y + groupNode.attrs.y
                _notifyCoverageCheck(image, {...imageProps, x, y})
            }
        }
    }

    const onDragMoveRect = (event) => {
        // Trigger coverage check while dragging (non-pattern / rect mode)
        if (image) _notifyCoverageCheck(image, event.target.attrs)
    }

    const boundBoxFunc = (oldBox, newBox) => {
        const minSize = Math.min(background.width, background.height) / 100
        if (newBox.width < minSize || newBox.height < minSize) {
            return oldBox
        }
        return newBox
    }

    const handleTransform = (event) => {
        const anchor = event.target.attrs
        const group = event.target.parent
        const children = group.getChildren()

        children.forEach((node, index) => {
            node.scaleX(anchor.scaleX)
            node.scaleY(anchor.scaleY)
            node.rotation(anchor.rotation)
        })
    }

    const handleDragMoveGroup = (event) => {
        if (!imageProps.hasPattern) return
        onShowToolbar()
    }

    const onDragGroupEnd = async (event) => {
        if (!imageProps.hasPattern) return

        const imageNode = imageRef.current
        const groupNode = groupRef.current
        if (!imageNode && !groupNode) return
        const x = imageNode.attrs.x + groupNode.attrs.x
        const y = imageNode.attrs.y + groupNode.attrs.y

        const imageAttrs = {
            ...imageProps,
            x,
            y,
        }
        groupRef.current?.setAttrs({ x: 0, y: 0 })
        onChange(imageAttrs)
        await PubSub.publish('UPDATE_3D_MODEL_TEXTURE')

        dispatch(actionGetClientRect(imageRef.current?.getClientRect()))
        if (image) _notifyCoverageCheck(image, imageAttrs)
    }

    // useEffect(() => {
    //     if (!trRef.current) return
    //     imageProps.rect = {
    //         x: trRef.current?.x(),
    //         y: trRef.current?.y(),
    //         width: trRef.current?.width(),
    //         height: trRef.current?.height(),
    //     }

    //     onChange(imageProps)
    // }, [trRef.current?.width(), trRef.current?.height(), trRef.current?.x(), trRef.current?.y()])

    return (
        <Fragment>
            {loading ? (
                <SpinnerLayer {...imageProps} productCreatedType={productCreatedType} selectedSide={selectedSide} />
            ) : (
                <Fragment>
                    <Group
                        onClick={() => onSelect({ ...imageProps })}
                        draggable={!isLock && isDraggable}
                        onDragStart={onDragStart}
                        ref={groupRef}
                        onDragMove={isLock ? undefined : onDragMove}
                        onDragEnd={isLock ? undefined : onDragGroupEnd}
                        dragBoundFunc={(pos) => {
                            const group = groupRef.current
                            const background = group?.getParent().children[0] // Assuming the background is the first child of the group's parent
                            const mainElement = imageRef.current

                            const { width: bgWidth, height: bgHeight } = background.getClientRect()

                            // Define your boundaries based on the background size and main element size
                            const minX = background.getAbsolutePosition().x - mainElement.getClientRect().width / 2
                            const maxX = minX + bgWidth + mainElement.getClientRect().width
                            const minY = background.getAbsolutePosition().y - mainElement.getClientRect().height / 2
                            const maxY = minY + bgHeight + mainElement.getClientRect().height

                            // Calculate the new position of the main element after the drag event
                            const mainX = pos.x + mainElement.getAbsolutePosition().x - group.getAbsolutePosition().x
                            const mainY = pos.y + mainElement.getAbsolutePosition().y - group.getAbsolutePosition().y

                            // Check if the new position of the main element is within the boundaries
                            const isWithinBoundaries = mainX >= minX && mainX <= maxX && mainY >= minY && mainY <= maxY

                            // If it's not within the boundaries, adjust the group position to the nearest valid position within the boundaries
                            if (!isWithinBoundaries) {
                                const posX = Math.max(
                                    minX - mainElement.getAbsolutePosition().x + group.getAbsolutePosition().x,
                                    Math.min(
                                        maxX - mainElement.getAbsolutePosition().x + group.getAbsolutePosition().x,
                                        pos.x
                                    )
                                )
                                const posY = Math.max(
                                    minY - mainElement.getAbsolutePosition().y + group.getAbsolutePosition().y,
                                    Math.min(
                                        maxY - mainElement.getAbsolutePosition().y + group.getAbsolutePosition().y,
                                        pos.y
                                    )
                                )

                                return { x: posX, y: posY }
                            }

                            // If it's within the boundaries, return the new position
                            return pos
                        }}
                        opacity={!isSelected && cropMode ? 0.24 : 1}
                    >
                        {grid.map((item, index) => (
                            <Rect
                                {...item}
                                visible={!cropMode || !isSelected}
                                draggable={false}
                                fillPatternImage={image}
                                globalCompositeOperation="source-atop"
                            />
                        ))}
                        <Rect
                            {...imageProps}
                            ref={imageRef}
                            draggable={!isLock && !imageProps.hasPattern}
                            listening={!isLock}
                            offsetX={
                                patternType === PATTERN_TYPE.HORIZONTAL ? imageProps.width / 2 : imageProps.offsetX
                            }
                            offsetY={patternType === PATTERN_TYPE.VERTICAL ? imageProps.height / 2 : imageProps.offsetY}
                            fillPatternImage={image}
                            globalCompositeOperation="source-atop"
                            onClick={isLock ? undefined : onItemClick}
                            onTap={isLock ? undefined : onItemClick}
                            onDragStart={isLock ? undefined : (e) => { perfHandlers.onDragStart(e); onItemClick(e); }}
                            onDragMove={isLock ? undefined : onDragMoveRect}
                            onDragEnd={isLock ? undefined : (e) => { perfHandlers.onDragEnd(e); onDragEnd(e); }}
                            onTransformStart={isLock ? undefined : perfHandlers.onTransformStart}
                            onTransformEnd={isLock ? undefined : (e) => { perfHandlers.onTransformEnd(e); onTransformEnd(e); }}
                            onTransform={isLock ? undefined : handleTransform}
                            onMouseEnter={() => { }}
                            name={'main'}
                            dragBoundFunc={
                                isLock
                                    ? undefined
                                    : (pos) => {
                                        const group = groupRef.current
                                        const background = group?.getParent().children[0] // Assuming the background is the first child of the group's parent
                                        const mainElement = imageRef.current.getClientRect()

                                        const { width: bgWidth, height: bgHeight } = background.getClientRect()
                                        const minX = background.getAbsolutePosition().x - mainElement.width / 2
                                        const maxX = minX + bgWidth + mainElement.width
                                        const minY = background.getAbsolutePosition().y - mainElement.height / 2
                                        const maxY = minY + bgHeight + mainElement.height
                                        return {
                                            x: Math.min(Math.max(pos.x, minX), maxX),
                                            y: Math.min(Math.max(pos.y, minY), maxY),
                                        }
                                    }
                            }
                            visible={!cropMode || !isSelected}
                        />
                    </Group>
                </Fragment>
            )}
            {!cropMode && isSelected && !loading && !isLock && (
                <Transformer
                    ref={trRef}
                    // keepRatio={true}
                    // centeredScaling={true}
                    anchorCornerRadius={30}
                    rotateAnchorOffset={20}
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                    boundBoxFunc={boundBoxFunc}
                    onTransform={onTransformRealTime}
                    rotateAnchorCursor="grabbing"
                    name="transformer"
                />
            )}
            {cropMode && isSelected && (
                <CropSelectorWithBounds selectedLayer={imageProps} dispatch={dispatch} trRef={trRef} />
            )}
        </Fragment>
    )
})

export default ImageLayer

